import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFounderJournalArchive,
  filterFounderJournalEntries,
  FounderJournalEntry,
  FounderJournalPeriodFilter
} from "../services/founderJournalService";

type FounderJournalState = {
  entries: FounderJournalEntry[];
  filteredEntries: FounderJournalEntry[];
  duplicateDateKeys: string[];
  error: string | null;
  isLoading: boolean;
  lastUpdated: Date | null;
  period: FounderJournalPeriodFilter;
  searchTerm: string;
  jumpDate: string;
  setPeriod: (period: FounderJournalPeriodFilter) => void;
  setSearchTerm: (searchTerm: string) => void;
  setJumpDate: (jumpDate: string) => void;
  refresh: () => Promise<void>;
};

export function useFounderJournal(): FounderJournalState {
  const [entries, setEntries] = useState<FounderJournalEntry[]>([]);
  const [duplicateDateKeys, setDuplicateDateKeys] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [period, setPeriod] = useState<FounderJournalPeriodFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [jumpDate, setJumpDate] = useState("");

  const refresh = useCallback(async () => {
    setIsLoading(true);

    try {
      const archive = await fetchFounderJournalArchive();
      setEntries(archive.entries);
      setDuplicateDateKeys(archive.duplicateDateKeys);
      setError(null);
      setLastUpdated(archive.lastUpdated);
    } catch {
      setEntries([]);
      setDuplicateDateKeys([]);
      setError("Unable to load Founder's Journal.");
      setLastUpdated(new Date());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filteredEntries = useMemo(
    () => filterFounderJournalEntries(entries, { period, searchTerm, jumpDate }),
    [entries, period, searchTerm, jumpDate]
  );

  return {
    entries,
    filteredEntries,
    duplicateDateKeys,
    error,
    isLoading,
    lastUpdated,
    period,
    searchTerm,
    jumpDate,
    setPeriod,
    setSearchTerm,
    setJumpDate,
    refresh
  };
}
